import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useCartContext } from '@/hooks/useCartContext';

const shopCategories = [
  { label: 'Cookware', href: '/products?category=Cookware' },
  { label: 'Dinnerware', href: '/products?category=Dinnerware' },
  { label: 'Cutlery', href: '/products?category=Cutlery' },
  { label: 'Storage', href: '/products?category=Storage' },
  { label: 'Appliances', href: '/products?category=Appliances' },
];

const collectionsLinks = [
  { label: 'New Arrivals', href: '/products?badge=New+Arrival' },
  { label: 'Best Sellers', href: '/products?badge=Best+Seller' },
  { label: 'Premium Line', href: '/products?badge=Premium' },
  { label: 'Top Rated', href: '/products?badge=Top+Rated' },
];

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);
  const [collectionsOpen, setCollectionsOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { totalItems, setIsOpen } = useCartContext();
  const location = useLocation();

  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const navTextColor = (!isHome || scrolled) ? 'text-stone-800' : 'text-white';
  const navBg = scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : (isHome ? 'bg-transparent' : 'bg-white shadow-sm');

  return (
    <header className={`fixed top-0 left-0 right-0 z-30 transition-all duration-300 ${navBg}`}>
      {/* Top bar */}
      <div className={`hidden md:flex items-center justify-center py-2 text-xs transition-colors ${scrolled || !isHome ? 'bg-stone-900 text-stone-300' : 'bg-black/30 text-white/80'}`}>
        <span>Free shipping on orders over $150 &nbsp;|&nbsp; zephyrionllc@outlook.com &nbsp;|&nbsp; Phoenix, AZ</span>
      </div>

      <nav className="px-6 lg:px-12 py-4 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 flex-shrink-0">
          <img
            src="https://public.readdy.ai/ai/img_res/7f4f4933-43ac-46f9-a6f6-a6a961fa216c.png"
            alt="ZEPHYRION LLC"
            className="h-10 w-auto object-contain"
          />
          <span className={`hidden lg:block font-bold text-lg tracking-widest transition-colors ${navTextColor}`} style={{ fontFamily: "'Playfair Display', serif" }}>
            ZEPHYRION
          </span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          {/* Shop dropdown */}
          <div
            className="relative"
            onMouseEnter={() => setShopOpen(true)}
            onMouseLeave={() => setShopOpen(false)}
          >
            <button className={`flex items-center gap-1 text-sm font-medium transition-colors hover:text-amber-500 cursor-pointer whitespace-nowrap ${navTextColor}`}>
              Shop <i className="ri-arrow-down-s-line text-xs" />
            </button>
            {shopOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-48 bg-white rounded-xl shadow-lg border border-stone-100 py-2 z-50">
                {shopCategories.map(cat => (
                  <Link
                    key={cat.label}
                    to={cat.href}
                    className="block px-4 py-2.5 text-sm text-stone-700 hover:bg-amber-50 hover:text-amber-600 transition-colors"
                  >
                    {cat.label}
                  </Link>
                ))}
                <div className="border-t border-stone-100 mt-1 pt-1">
                  <Link to="/products" className="block px-4 py-2.5 text-sm font-medium text-amber-600 hover:bg-amber-50 transition-colors">
                    View All Products
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* Collections dropdown */}
          <div
            className="relative"
            onMouseEnter={() => setCollectionsOpen(true)}
            onMouseLeave={() => setCollectionsOpen(false)}
          >
            <button className={`flex items-center gap-1 text-sm font-medium transition-colors hover:text-amber-500 cursor-pointer whitespace-nowrap ${navTextColor}`}>
              Collections <i className="ri-arrow-down-s-line text-xs" />
            </button>
            {collectionsOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-48 bg-white rounded-xl shadow-lg border border-stone-100 py-2 z-50">
                {collectionsLinks.map(col => (
                  <Link
                    key={col.label}
                    to={col.href}
                    className="block px-4 py-2.5 text-sm text-stone-700 hover:bg-amber-50 hover:text-amber-600 transition-colors"
                  >
                    {col.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <Link to="/about" className={`text-sm font-medium transition-colors hover:text-amber-500 whitespace-nowrap ${navTextColor}`}>
            About
          </Link>
          <Link to="/products" className={`text-sm font-medium transition-colors hover:text-amber-500 whitespace-nowrap ${navTextColor}`}>
            Kitchen Guide
          </Link>
          <a href="mailto:zephyrionllc@outlook.com" className={`text-sm font-medium transition-colors hover:text-amber-500 whitespace-nowrap ${navTextColor}`}>
            Contact
          </a>
        </div>

        {/* Right icons */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsOpen(true)}
            className={`relative w-9 h-9 flex items-center justify-center rounded-full hover:bg-white/20 transition-colors cursor-pointer ${navTextColor}`}
          >
            <i className="ri-shopping-cart-line text-lg" />
            {totalItems > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-amber-500 text-white text-xs font-bold w-4 h-4 rounded-full flex items-center justify-center leading-none">
                {totalItems > 9 ? '9+' : totalItems}
              </span>
            )}
          </button>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden w-9 h-9 flex items-center justify-center rounded-full hover:bg-white/20 transition-colors cursor-pointer ${navTextColor}`}
          >
            <i className={`text-lg ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-stone-100 px-6 py-4 space-y-1">
          <p className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-2">Shop</p>
          {shopCategories.map(cat => (
            <Link key={cat.label} to={cat.href} className="block py-2 text-sm text-stone-700 hover:text-amber-600">
              {cat.label}
            </Link>
          ))}
          <div className="border-t border-stone-100 pt-3 mt-3 space-y-1">
            <Link to="/products" className="block py-2 text-sm text-stone-700 hover:text-amber-600">All Products</Link>
            <Link to="/about" className="block py-2 text-sm text-stone-700 hover:text-amber-600">About</Link>
            <a href="mailto:zephyrionllc@outlook.com" className="block py-2 text-sm text-stone-700 hover:text-amber-600">Contact</a>
          </div>
        </div>
      )}
    </header>
  );
}
